import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import * as Y from 'yjs';
import * as awarenessProtocol from 'y-protocols/awareness';
import { useDiagramWithElectricProvider } from '../hooks/useDiagramWithElectricProvider';
import { useDiagramAccess } from '../hooks/useDiagramAccess';
import { useDiagramInfo, type DiagramType } from '../hooks/useDiagramInfo';
import { useFolderArchived } from '../hooks/useFolderArchived';
import { useAuthenticatedFetch } from '../hooks/useAuth';
import type { Shape, Connector } from '../schemas/diagramSchemas';
import type { SyncMode } from '../lib/electric/ElectricYjsProvider';
import { getNodeMutationManager } from '../core/commands/mutations/NodeMutationManager';
import { ShapeMapAdapter } from '../lib/diagram/ShapeMapAdapter';
import { ShapePersistenceGateway } from '../lib/diagram/shapeGeometry';
import { useDiagramStore } from '../stores/diagramStore';
import { useNavigationStore } from '../stores/navigationStore';
import { Logger } from '../utils/logger';
import { LocalDocBackupStore } from '../lib/persistence/LocalDocBackupStore';

interface DiagramDocumentContextValue {
  diagramId: string;
  folderId?: string | null;
  doc: Y.Doc | null;
  awareness: awarenessProtocol.Awareness | null;
  provider: any; // Kept for backward compatibility (always null now)
  isConnected: boolean;
  isSynced: boolean;
  isSyncing: boolean;
  pendingCount?: number;
  saveStatus: 'idle' | 'saving' | 'saved' | 'error';
  documentError: string | null;
  hasAccess: boolean;
  accessError: string | null;
  isLoadingAccess: boolean;
  canWrite: boolean; // User's write permission
  userRole: string; // User's role (viewer, editor, etc.)
  syncMode: SyncMode; // Solo/collab mode for broadcaster gating (C1)
  diagramType: DiagramType; // The type of diagram (genogram, org, etc.)
  isDiagramTypeResolved: boolean;
  flushPendingUpdates: () => Promise<void>;
  // Mutation helpers (Yjs CRDT operations)
  updateShape: (shapeId: string, updates: Partial<Shape>) => void;
  deleteShape: (shapeId: string) => void;
  addShape: (shape: Shape) => void;
  updateConnector: (connectorId: string, updates: Partial<Connector>) => void;
  deleteConnector: (connectorId: string) => void;
  addConnector: (connector: Connector) => void;
  downloadBackup: () => void;
}

const DiagramDocumentContext = createContext<DiagramDocumentContextValue | null>(null);

/** Exposed for lightweight wrappers (e.g. read-only viewer) that need to satisfy
 *  child components calling useDiagramDocument() without the full provider. */
export { DiagramDocumentContext };

const TEST_DIAGRAM_TYPES = new Set<DiagramType>([
  'genogram',
  'flowchart',
  'mindmap',
  'network',
  'org',
  'database',
  'uml',
  'generic',
]);

function getTestDiagramType(): DiagramType | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const value = params.get('diagram') ?? params.get('type');
  return value && TEST_DIAGRAM_TYPES.has(value as DiagramType) ? (value as DiagramType) : null;
}

interface DiagramDocumentProviderProps {
  diagramId: string;
  userId: string;
  userName: string;
  children: React.ReactNode;
}

/**
 * Provider for diagram document collaboration
 *
 * Architecture (Hybrid: Electric SQL + WebSocket):
 * - Uses useDiagramWithElectricProvider hook
 * - Yjs for CRDT writes (automatic conflict resolution)
 * - Electric SQL Shape Streams for document updates (HTTP streaming, persistent)
 *   → Stored in diagram_updates table, replayed on reconnect
 *   → ~50-100ms latency (acceptable for persistence)
 * - WebSocket for awareness (stateless relay, ephemeral)
 *   → Cursors, selections, live previews
 *   → ~10-50ms latency (fast for UX)
 * - Real-time collaboration: 10-100ms (vs 0-5s with polling)
 */
export const DiagramDocumentProvider: React.FC<DiagramDocumentProviderProps> = ({
  diagramId,
  userId,
  userName,
  children,
}) => {
  // Check access permissions - bypass for test mode
  // Test mode when explicit test diagram id OR URL flag ?test=true
  const isTestMode = diagramId === 'test' || (
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('test') === 'true'
  );
  const { data: accessInfo, error: accessError, isLoading: accessLoading } = 
    useDiagramAccess(isTestMode ? undefined : diagramId);
  
  // Fetch diagram metadata including type
  const { data: diagramInfo } = useDiagramInfo(isTestMode ? undefined : diagramId);
  const folderId = isTestMode ? null : diagramInfo?.folderId ?? null;
  const diagramType: DiagramType = diagramInfo?.type ?? (isTestMode ? getTestDiagramType() : null) ?? 'genogram';
  const isDiagramTypeResolved = isTestMode || Boolean(diagramInfo?.type);

  // Determine if user has access
  const hasAccess = isTestMode || (!accessLoading && !accessError && accessInfo?.role !== 'none');
  const finalAccessError = accessError?.message || 
    (!hasAccess && !accessLoading ? 'Access denied - no permission to view this diagram' : null);

  // Detect if we're in link mode (access via link token)
  // Memoize to prevent unnecessary provider recreations
  const isLinkMode = React.useMemo(() => accessInfo?.source === 'link', [accessInfo?.source]);

  // Electric SQL + Yjs hook (only connect if user has access)
  const syncEnabled = hasAccess && !isTestMode;
  
  const urlReadonly = React.useMemo(() => {
    if (typeof window === 'undefined') return false;
    const v = new URLSearchParams(window.location.search).get('readonly');
    return v === '1' || v === 'true';
  }, []);

  // A saved People view can be shared directly without sharing its folder, so
  // it must not query folder state. Its write access still follows the
  // diagram role: editors change only its own presentation settings, and the
  // saved-view guards (tool registry, property panel, projection lock) block
  // every other mutation.
  const isSavedAtlasView = diagramInfo?.viewDefinition?.type === 'saved-people-view';

  // Diagrams inside an archived folder are read-only until the folder is
  // restored — opening one directly via URL must not allow edits.
  const { archived: isFolderArchived } = useFolderArchived(
    isSavedAtlasView ? undefined : folderId ?? undefined,
  );

  const isReadonlyMode = urlReadonly || isFolderArchived;

  // Memoize canWrite to prevent unnecessary provider recreations
  const canWrite = React.useMemo(() => accessInfo?.canWrite ?? false, [accessInfo?.canWrite]);
  const effectiveCanWrite = React.useMemo(() => (isReadonlyMode ? false : canWrite), [canWrite, isReadonlyMode]);
  const authenticatedFetch = useAuthenticatedFetch();
  
  const syncResult = useDiagramWithElectricProvider({
    diagramId,
    userId,
    userName,
    isLinkMode,
    canWrite: effectiveCanWrite,
  });

  // For test mode, create a simple local doc
  const [testDoc] = useState(() => isTestMode ? new Y.Doc() : null);
  const [testAwareness] = useState(() => isTestMode ? new awarenessProtocol.Awareness(testDoc!) : null);

  // Use either sync or test mode values
  const doc = syncEnabled ? syncResult.ydoc : testDoc;
  const awareness = syncEnabled ? syncResult.awareness : testAwareness;
  const isConnected = syncEnabled ? syncResult.isConnected : true;
  const isReady = syncEnabled ? syncResult.isReady : true;
  const isSyncing = syncEnabled ? (syncResult as any).isSyncing : false;
  const pendingCount = syncEnabled ? (syncResult as any).pendingCount : 0;
  const saveStatus = syncEnabled ? syncResult.saveStatus : ('idle' as const);
  const documentError = syncEnabled ? syncResult.error?.message ?? null : null;
  const syncModeValue: SyncMode = syncEnabled ? syncResult.syncMode ?? 'solo' : 'solo';
  const flushPendingUpdates = syncEnabled ? syncResult.flushPendingUpdates : async () => {};

  // Set Yjs document on NodeMutationManager for commands to use
  // IMPORTANT: Always set the ydoc from syncResult even when syncEnabled is false,
  // because the ydoc is valid for local operations. The syncEnabled flag only affects
  // whether the provider syncs with the server, not whether the local ydoc is usable.
  // Without this, deletes only go to the Zustand store and shapes reappear on next sync.
  useEffect(() => {
    const mutationManager = getNodeMutationManager();
    // Use syncResult.ydoc directly instead of doc, which is null when syncEnabled is false
    const ydocForMutations = isTestMode ? testDoc : syncResult.ydoc;
    mutationManager.setYDoc(ydocForMutations ?? undefined);
    mutationManager.canWrite = isTestMode ? true : effectiveCanWrite;
    Logger.debug('DiagramDocumentProvider', 'Set Yjs doc on NodeMutationManager:', !!ydocForMutations, 'canWrite:', isTestMode ? true : effectiveCanWrite);
  }, [syncResult.ydoc, testDoc, effectiveCanWrite, isTestMode]);
  
  // Set write permission on the diagram store
  useEffect(() => {
    const store = useDiagramStore.getState();
    store.setCanWrite(isTestMode ? true : effectiveCanWrite);
    Logger.debug('DiagramDocumentProvider', 'Set canWrite on diagram store:', isTestMode ? true : effectiveCanWrite);
  }, [effectiveCanWrite, isTestMode]);

  // Initialize breadcrumb stack:
  // 1) Prefer an explicit navigation stack handoff from the previous page load (drill-down/back)
  // 2) Otherwise, rebuild a best-effort chain by following referenced-by links (pick first parent at each step)
  useEffect(() => {
    let cancelled = false;

    function setStack(items: Array<{ diagramId?: unknown; diagramName?: unknown; folderId?: unknown; sourceShapeId?: unknown }>) {
      const sanitized = items
        .map((it) => ({
          diagramId: String(it.diagramId || '').trim(),
          diagramName: String(it.diagramName || '').trim(),
          folderId: String(it.folderId || '').trim(),
          sourceShapeId: it.sourceShapeId ? String(it.sourceShapeId) : undefined,
        }))
        .filter((it) => it.diagramId && it.folderId)
        .map((it) => ({
          ...it,
          diagramName: it.diagramName || 'Untitled Diagram',
        }));

      useNavigationStore.setState({
        breadcrumbStack: sanitized,
        isNavigating: false,
        pendingNavigation: null,
      });
    }

    async function initBreadcrumbs() {
      // 1) Navigation handoff from previous page
      try {
        const raw = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('diagram-nav-stack') : null;
        if (raw) {
          const parsed = JSON.parse(raw) as { targetDiagramId?: string; breadcrumbStack?: unknown };
          if (parsed?.targetDiagramId === diagramId && Array.isArray(parsed.breadcrumbStack)) {
            setStack(parsed.breadcrumbStack as any[]);
            sessionStorage.removeItem('diagram-nav-stack');
            sessionStorage.removeItem('diagram-nav-last'); // legacy
            return;
          }
          // Drop stale handoff
          sessionStorage.removeItem('diagram-nav-stack');
        }
      } catch {}

      // For fresh loads, always rebuild to avoid stale persisted stacks
      useNavigationStore.setState({ breadcrumbStack: [], isNavigating: false, pendingNavigation: null });

      if (isTestMode || !hasAccess || accessLoading) {
        return;
      }

      const maxDepth = 10;
      const chain: Array<{ diagramId: string; diagramName: string; folderId: string }> = [];
      const visited = new Set<string>([diagramId]);
      let cursor = diagramId;

      for (let depth = 0; depth < maxDepth; depth += 1) {
        if (cancelled) return;

        const refsResp = await authenticatedFetch(`/api/diagrams/${encodeURIComponent(cursor)}/referenced-by`);
        if (!refsResp.ok) break;
        const refsJson = await refsResp.json().catch(() => ({} as any));
        const links = Array.isArray((refsJson as any).links) ? ((refsJson as any).links as any[]) : [];
        if (links.length === 0) break;

        const parentIdRaw = links[0]?.sourceDiagramId;
        const parentId = typeof parentIdRaw === 'string' ? parentIdRaw : parentIdRaw != null ? String(parentIdRaw) : '';
        if (!parentId || visited.has(parentId)) break;
        visited.add(parentId);

        const parentResp = await authenticatedFetch(`/api/diagrams/${encodeURIComponent(parentId)}`);
        if (!parentResp.ok) break;
        const parentJson = await parentResp.json().catch(() => ({} as any));
        const name = String((parentJson as any).diagram?.name || 'Untitled Diagram');
        const folderId = String((parentJson as any).diagram?.folderId || '');
        if (!folderId) break;

        chain.push({ diagramId: parentId, diagramName: name, folderId });
        cursor = parentId;
      }

      chain.reverse();
      if (!cancelled) {
        setStack(chain);
      }
    }

    initBreadcrumbs();
    return () => {
      cancelled = true;
    };
  }, [diagramId, isTestMode, hasAccess, accessLoading, authenticatedFetch]);

  // No minimum loading time - show layout immediately

  // Mutation helpers for Yjs operations
  const updateShape = useCallback((shapeId: string, updates: Partial<Shape>) => {
    if (!doc) return;
    
    const shapesMap = new ShapeMapAdapter(doc.getMap('shapes'));
    const shapeGateway = new ShapePersistenceGateway(shapesMap);
    const current = shapesMap.get(shapeId) as Shape | undefined;
    
    if (current) {
      doc.transact(() => {
        shapeGateway.patch(shapeId, updates);
      }, 'local');
    }
  }, [doc]);

  const deleteShape = useCallback((shapeId: string) => {
    if (!doc) return;

    const shapesMap = new ShapeMapAdapter(doc.getMap('shapes'));
    const shapeGateway = new ShapePersistenceGateway(shapesMap);
    doc.transact(() => {
      shapeGateway.delete(shapeId);
    }, 'local');
  }, [doc]);

  const addShape = useCallback((shape: Shape) => {
    if (!doc) return;

    const shapesMap = new ShapeMapAdapter(doc.getMap('shapes'));
    const shapeGateway = new ShapePersistenceGateway(shapesMap);
    doc.transact(() => {
      shapeGateway.create(shape);
    }, 'local');
  }, [doc]);

  const updateConnector = useCallback((connectorId: string, updates: Partial<Connector>) => {
    if (!doc) return;

    const connectorsMap = doc.getMap('connectors');
    const current = connectorsMap.get(connectorId) as Connector | undefined;

    if (current) {
      doc.transact(() => {
        connectorsMap.set(connectorId, { ...current, ...updates });
      }, 'local');
    }
  }, [doc]);

  const deleteConnector = useCallback((connectorId: string) => {
    if (!doc) return;

    doc.transact(() => {
      doc.getMap('connectors').delete(connectorId);
    }, 'local');
  }, [doc]);

  const addConnector = useCallback((connector: Connector) => {
    if (!doc) return;

    doc.transact(() => {
      doc.getMap('connectors').set(connector.id, connector);
    }, 'local');
  }, [doc]);

  const downloadBackup = useCallback(() => {
    const activeDoc = isTestMode ? testDoc : syncResult.ydoc;
    if (!activeDoc) return;
    LocalDocBackupStore.save(diagramId, activeDoc, 'manual', null).catch(() => {});
    const update = Y.encodeStateAsUpdate(activeDoc);
    const blob = new Blob([update], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `diagram-${diagramId}-backup-${Date.now()}.crly`;
    a.click();
    URL.revokeObjectURL(url);
  }, [isTestMode, testDoc, syncResult.ydoc, diagramId]);

  // Context value
  const contextValue: DiagramDocumentContextValue = {
    diagramId,
    folderId,
    doc,
    awareness,
    provider: null, // No longer using provider pattern
    isConnected,
    isSynced: isReady,
    isSyncing,
    pendingCount,
    saveStatus,
    documentError,
    hasAccess,
    accessError: finalAccessError,
    isLoadingAccess: accessLoading,
    // In test mode, allow writes unconditionally
    canWrite: isTestMode ? true : effectiveCanWrite,
    userRole: accessInfo?.role ?? 'none',
    syncMode: syncModeValue,
    diagramType,
    isDiagramTypeResolved,
    flushPendingUpdates,
    updateShape,
    deleteShape,
    addShape,
    updateConnector,
    deleteConnector,
    addConnector,
    downloadBackup,
  };

  return (
    <DiagramDocumentContext.Provider value={contextValue}>
      {children}
    </DiagramDocumentContext.Provider>
  );
};

/**
 * Hook to access diagram document context
 */
export const useDiagramDocument = (): DiagramDocumentContextValue => {
  const context = useContext(DiagramDocumentContext);
  if (!context) {
    throw new Error('useDiagramDocument must be used within DiagramDocumentProvider');
  }
  return context;
};

/**
 * Non-throwing variant of `useDiagramDocument`. Returns `null` when the
 * hook is called outside a `DiagramDocumentProvider` — useful for hooks
 * that *can* function in either a diagram-doc context or a folder-only
 * context (e.g. compass baseline state lives on the folder Yjs doc when
 * a diagram is open, but the folder dashboard renders without one and
 * those hooks should degrade gracefully rather than crash the tree).
 */
export const useOptionalDiagramDocument = (): DiagramDocumentContextValue | null => {
  return useContext(DiagramDocumentContext);
};
